// frontend/src/pages/LandingPage/LandingPage.jsx

import React, { useCallback, useEffect, useRef, useState, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Tabs, Tab, IconButton, Chip, Box, Typography, Button, TextField, Tooltip, InputAdornment } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { motion } from 'framer-motion';
import Particles from 'react-tsparticles';
import { loadSlim } from 'tsparticles-slim';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import UniversalNav from '../../components/UniversalNav/UniversalNav';
import ImageIcon from '@mui/icons-material/Image';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import TheatersIcon from '@mui/icons-material/Theaters';
import ShieldIcon from '@mui/icons-material/Shield';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DangerousIcon from '@mui/icons-material/Dangerous';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SecurityIcon from '@mui/icons-material/Security';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import './LandingPage.css';
import { UIContext } from '../../contexts/UIContext';
import DynamicHeroImage from './DynamicHeroImage';

// This animates children without fading the parent wrapper itself
const staggerContainer = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    },
  },
};

const sentenceVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      delay: 0.2,
      staggerChildren: 0.04, // Fast stagger for a fluid effect
    },
  },
};

const letterVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      damping: 12,
      stiffness: 200,
    },
  },
};

// --- SECTION ANIMATION VARIANTS ---
// Using scale and custom easing for a premium "breathe-in" modern feel
// Avoids 'y' translation to protect the backdrop-filter glass effect
const sectionVariants = {
  hidden: { 
      opacity: 0, 
      scale: 0.96 
  },
  visible: { 
      opacity: 1, 
      scale: 1,
      transition: { 
          duration: 1.2, // Slower, more cinematic transition
          ease: [0.22, 1, 0.36, 1] // Custom modern easing curve (easeOutCubic/Quart hybrid)
      } 
  },
  exit: { 
      opacity: 0, 
      scale: 1.02, 
      transition: { 
          duration: 0.8,
          ease: "easeOut"
      } 
  }
};

// --- URL VALIDATION HELPER ---
const isValidUrl = (urlString) => {
    if (!urlString || typeof urlString !== "string" || urlString.trim().length === 0) {
        return false;
    }
    try {
        const url = new URL(urlString);
        return url.protocol === "http:" || url.protocol === "https:";
    } catch (_) {
        return false;
    }
};

// --- FRAUD TICKER COMPONENT ---
const FraudTicker = () => {
    const tickerRef = useRef(null);
    const { t } = useTranslation();
    // Get dynamic year
    const currentYear = new Date().getFullYear();

    useEffect(() => {
        // $172 Billion / Year projection
        const RATE_PER_SECOND = 5454.0842212;
        
        // Target: Start of the current year (Dynamic)
        const startOfYear = new Date(currentYear, 0, 1).getTime();
        
        // Animation Configuration
        const animDuration = 5000; // 5 seconds
        const startTime = performance.now();
        
        const updateTicker = () => {
            const now = new Date().getTime();
            const nowPerf = performance.now();
            
            // 1. Calculate the 'Real' accumulated value for this year
            const secondsElapsed = (now - startOfYear) / 1000;
            const realTotal = secondsElapsed * RATE_PER_SECOND;

            // 2. Calculate Animation Progress (0.0 to 1.0)
            const timeSinceStart = nowPerf - startTime;
            let progress = Math.min(timeSinceStart / animDuration, 1);
            
            // 3. Apply Easing
            const ease = 1 - Math.pow(1 - progress, 3);
            
            // 4. Determine displayed value
            const displayedValue = realTotal * ease;

            if (tickerRef.current) {
                tickerRef.current.innerText = new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    minimumFractionDigits: 2
                }).format(displayedValue);
            }

            requestAnimationFrame(updateTicker);
        };

        const frameId = requestAnimationFrame(updateTicker);
        return () => cancelAnimationFrame(frameId);
    }, [currentYear]);

    return (
        <div className="fraud-ticker-container">
            {/* UPDATED LABEL WITH DYNAMIC YEAR */}
            <Typography className="ticker-label">
                {t('landingPage.problem.tickerLabel', { 
                    defaultValue: `YTD (${currentYear}): Annual Global Cost of Misinformation, Ad Fraud, & Deepfakes`,
                    year: currentYear 
                })}
            </Typography>
            
            <div ref={tickerRef} className="ticker-value">
                $0.00
            </div>

        </div>
    );
};

// --- VETRA ID SEARCH & AUDIT DROPZONE COMPONENT ---
const VetraIdSearch = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { openDocViewer } = useContext(UIContext); // For documentation tooltip

    // UI States
    const [activeTab, setActiveTab] = useState(0); // 0 = Vetra-ID, 1 = Media Audit
    const [isProcessing, setIsProcessing] = useState(false);
    const [statusMessage, setStatusMessage] = useState('');
    const [isError, setIsError] = useState(false);
    const [showDeepfakeCTA, setShowDeepfakeCTA] = useState(false);
    const [idValidationError, setIdValidationError] = useState('');
    const [hasAudited, setHasAudited] = useState(false);
    const [isAuditResult, setIsAuditResult] = useState(false);
    const [ctaType, setCtaType] = useState('unverified'); // 'unverified' or 'audio_tampered'
    
    // Data States
    const [searchId, setSearchId] = useState(''); // For Tab 0
    const [mediaInput, setMediaInput] = useState(''); // For Tab 1 (URL string or File object)
    const [mediaFileName, setMediaFileName] = useState(''); // To display chip
    
    const [isDragOver, setIsDragOver] = useState(false);
    const fileInputRef = useRef(null);

    // --- TAB SWITCHER ---
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
        setStatusMessage('');
        setIsError(false);
        setShowDeepfakeCTA(false);
        setIdValidationError('');
        setHasAudited(false);
        setIsAuditResult(false);
        setCtaType('unverified');
    };

    // --- TAB 0: MANUAL ID SEARCH ---
    const handleIdSearch = async (e) => {
        e.preventDefault();
        setStatusMessage('');
        setIsError(false);
        setShowDeepfakeCTA(false);
        setIsAuditResult(false);
        const cleanId = searchId.trim();
        if (!cleanId) return;

        // Strict validation: Vetra ID must be exactly 8 characters
        if (cleanId.length !== 8) {
            setIsError(true);
            setStatusMessage(t('landingPage.hero.idLengthError', 'A Vetra-ID must be exactly 8 characters long.'));
            return;
        }

        setIsProcessing(true);
        try {
            if (cleanId.length > 30) {
                // Long Hash logic...
            } else {
                // 2. IT'S A SHORT VETRA-ID
                const apiCheckUrl = `/api/v/${encodeURIComponent(cleanId)}?_t=${Date.now()}`;
                
                const response = await fetch(apiCheckUrl, { 
                    method: 'GET',
                    headers: { 'Accept': 'application/json' },
                    credentials: 'include' // Allows the browser to save the cookie
                }); 
                
                if (response.ok) {
                    const data = await response.json();
                    
                    if (data.valid === true) {
                        window.open(`/api/v/${encodeURIComponent(cleanId)}`, '_blank', 'noopener,noreferrer');
                        setSearchId('');
                    } else {
                        setIsError(true);
                        setStatusMessage(t('landingPage.hero.vetraIdErrorNotFound', "We couldn't find a record for this Vetra-ID. Please check for typos and try again."));
                    }
                } else {
                    throw new Error('Server returned an unexpected status');
                }
            }
        } catch (err) {
            console.error("Search failed:", err);
            setIsError(true);
            setStatusMessage(t('landingPage.hero.vetraIdErrorNetwork', "Unable to connect to verification servers. Please try again."));
        } finally {
            setIsProcessing(false);
        }
    };

    // --- TAB 1: MEDIA AUDIT (URL or File) ---
    const processMediaAudit = async (fileOrUrl) => {
        if (!fileOrUrl) return;

        // Pre-flight check: Prevent sending >50MB files over fetch
        const MAX_FILE_SIZE = 50 * 1024 * 1024;
        if (fileOrUrl instanceof File && fileOrUrl.size > MAX_FILE_SIZE) {
            setIsError(true);
            setStatusMessage(t('landingPage.hero.errors.file_too_large', "File exceeds 50MB limit."));
            setHasAudited(true);
            return;
        }

        setIsProcessing(true);
        setShowDeepfakeCTA(false);
        setIsAuditResult(false);
        setStatusMessage(t('landingPage.hero.scanningWatermark', "Scanning for Invisible Watermark..."));
        setIsError(false);

        const formData = new FormData();
        if (typeof fileOrUrl === 'string') {
            formData.append('url', fileOrUrl);
        } else {
            formData.append('file', fileOrUrl);
        }

        try {
            const response = await fetch('/api/media/extract-watermark', {
                method: 'POST',
                body: formData,
                credentials: 'include' // <--- Allows the browser to store the bypass cookie!
            });

            const data = await response.json();

            if (response.status === 200 && data.status === 'authentic') {
                // SUCCESS: Watermark found, asset is authentic! 
                setIsAuditResult(true);
                setStatusMessage(t('landingPage.hero.watermarkFound', "Authentic Asset Detected! Opening verification..."));
                
                // OPEN IN A NEW TAB INSTEAD OF OVERWRITING THE CURRENT TAB
                window.open(data.redirect_url, '_blank', 'noopener,noreferrer'); 
                
                // Reset the dropzone status after 3 seconds so the user can audit another file
                // setTimeout(() => {
                //     setStatusMessage('');
                // }, 3000);
            } 
            else if (response.status === 422 && data.error_code === 'tampered') {
                // SCAM DETECTED: Watermark found, but pHash distance failed.
                setIsAuditResult(true);
                setIsError(true);
                setStatusMessage(t('landingPage.hero.watermarkTampered', "MANIPULATION DETECTED: This asset contains a brand watermark but has been structurally altered."));
            }
            // AUDIO TAMPER BLOCK
            else if (response.status === 422 && data.error_code === 'tampered_audio') {
                setIsAuditResult(true);
                setIsError(true);
                // Display the dynamic message from the backend (distinguishes stripped/muted vs replaced)
                const audioErrorMessage = data.message || t('landingPage.hero.watermarkTamperedAudio', "MANIPULATION DETECTED: The visual video is authentic, but the audio track has been maliciously replaced.");
                setStatusMessage(audioErrorMessage);
                // Reveal the CTA so they can run the Deepfake scan on the audio
                setCtaType('audio_tampered');
                setShowDeepfakeCTA(true); 
            }
            else if (response.status >= 400 && data.error_code) {
                 setIsError(true);
                 // Translate standard backend errors
                 setStatusMessage(t(`landingPage.hero.errors.${data.error_code}`, data.error));
            } else {
                // UNVERIFIED: No watermark found -> Trigger Lead Gen Pivot
                setIsAuditResult(true);
                setIsError(false);
                setStatusMessage(''); // Clear standard status
                setCtaType('unverified');
                setShowDeepfakeCTA(true); // Show the specific CTA box
            }
        } catch (err) {
            setIsError(true);
            setStatusMessage(t('landingPage.hero.watermarkNetworkError', "Network error during file scan."));
        } finally {
            setIsProcessing(false);
            setHasAudited(true); // Lock the button after scanning
        }
    };

    const handleMediaSubmit = (e) => {
        e.preventDefault();
        processMediaAudit(mediaInput);
    };

    // --- FILE HANDLING LOGIC ---
    const handleFileSelection = (file) => {
        if (!file) return;
        
        // 1. Check file type (must be image or video)
        if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
            setIsError(true);
            setStatusMessage(t('landingPage.hero.errors.invalid_format', "Invalid file type. Please upload an image or video."));
            return;
        }

        // 1b. Check file size (50MB Limit) to prevent TCP stream aborts
        const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
        if (file.size > MAX_FILE_SIZE) {
            setIsError(true);
            setStatusMessage(t('landingPage.hero.errors.file_too_large', "File exceeds 50MB limit."));
            if (fileInputRef.current) fileInputRef.current.value = "";
            return;
        }

        // 2. Clear old states
        setIsError(false);
        setStatusMessage('');
        setShowDeepfakeCTA(false);
        setHasAudited(false);
        setIsAuditResult(false);

        // 3. Set File & Display Name
        setMediaInput(file);
        setMediaFileName(file.name);

    };

    const clearMedia = () => {
        setMediaInput('');
        setMediaFileName('');
        setStatusMessage('');
        setIsError(false);
        setShowDeepfakeCTA(false);
        setHasAudited(false);
        setIsAuditResult(false);
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    // Drag and Drop Handlers
    const handleDragOver = (e) => {
        e.preventDefault();
        if (activeTab !== 1) setActiveTab(1); // Auto-switch to audit tab if dragging
        setIsDragOver(true);
    };
    const handleDragLeave = (e) => {
        e.preventDefault();
        setIsDragOver(false);
    };
    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragOver(false);
        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            handleFileSelection(e.dataTransfer.files[0]);
        }
    };

    // Shared Modern Tab Style
    const tabStyles = { 
        minHeight: '36px', 
        py: 0.5, px: 2, 
        borderRadius: '20px',
        textTransform: 'none',
        fontWeight: 600,
        fontSize: '0.9rem',
        color: 'rgba(255,255,255,0.6)', 
        zIndex: 1,
        '&.Mui-selected': { 
            color: 'white', 
            background: 'rgba(255,255,255,0.15)',
            boxShadow: '0 2px 10px rgba(0,0,0,0.2)'
        },
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
    };

    // Shared Clear Button Style
    const clearButtonStyles = { 
        minWidth: '28px', width: '28px', height: '28px', p: 0, borderRadius: '8px',
        background: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.8)',
        '&:hover': { background: 'rgba(255,82,82,0.8)', color: 'white' } 
    };

    useEffect(() => {
        // Check if the extension sent an audit_url parameter
        const params = new URLSearchParams(location.search);
        const auditUrl = params.get('audit_url');

        if (auditUrl && isValidUrl(auditUrl)) { // (Make sure you have a basic isValidUrl helper)
            // 1. Switch to the Media Audit Tab
            setActiveTab(1); 
            // 2. Populate the input box
            setMediaInput(auditUrl);

            // 3. Clean up the URL so it doesn't re-trigger on refresh
            window.history.replaceState(null, '', window.location.pathname);
        }
    }, [location.search]);

    // --- AUTO-DISMISS STATUS MESSAGE AFTER 5 SECONDS ---
    useEffect(() => {
        // Do not auto-dismiss while actively scanning
        if (!statusMessage || isProcessing || isAuditResult) return;

        const timer = setTimeout(() => {
            setStatusMessage('');
            setIsError(false);
        }, 5000); // 5000ms = 5 seconds (adjust to 3000 or 4000 if you want it faster)

        return () => clearTimeout(timer);
    }, [statusMessage, isProcessing, isAuditResult]);

    // Optional: Auto-dismiss ID validation errors as well
    useEffect(() => {
        if (!idValidationError) return;

        const timer = setTimeout(() => {
            setIdValidationError('');
        }, 5000);

        return () => clearTimeout(timer);
    }, [idValidationError]);

    return (
        <Box 
            className="hero-search-wrapper" 
            sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', maxWidth: '600px', margin: '3.5rem auto 0' }}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
        >
            <motion.div variants={sectionVariants} style={{ width: '100%' }}>
                
                {/* MODERN TABS SWITCHER */}
                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    <Tabs 
                        value={activeTab} 
                        onChange={handleTabChange} 
                        TabIndicatorProps={{ style: { display: 'none' } }} // Hide default indicator for a pill look
                        sx={{ 
                            minHeight: '44px',
                            background: 'rgba(0,0,0,0.2)',
                            borderRadius: '24px',
                            padding: '4px',
                            border: '1px solid rgba(255,255,255,0.08)'
                        }}
                    >
                        <Tab 
                            icon={
                                <Box sx={{
                                    width: 18, height: 18, mr: 0.5,
                                    backgroundColor: 'currentColor',
                                    maskImage: 'url(/static/previews/badge_template.png)',
                                    WebkitMaskImage: 'url(/static/previews/badge_template.png)',
                                    maskSize: 'contain', WebkitMaskSize: 'contain',
                                    maskRepeat: 'no-repeat', WebkitMaskRepeat: 'no-repeat',
                                    maskPosition: 'center', WebkitMaskPosition: 'center'
                                }} />
                            } 
                            iconPosition="start" 
                            label={t('landingPage.hero.tabVetraId', "Verify Vetra-ID")} 
                            sx={tabStyles} 
                        />
                        <Tab 
                            icon={<VisibilityOffIcon sx={{ fontSize: '1.2rem', mr: 0.5 }} />}
                            iconPosition="start" 
                            label={t('landingPage.hero.tabWatermarkAudit', "Audit Secured Asset")}
                            sx={tabStyles} 
                        />
                    </Tabs>
                </Box>

                {/* TAB 0: VETRA ID SEARCH */}
                {activeTab === 0 && (
                    <form onSubmit={handleIdSearch} className="hero-search-form" style={{ maxWidth: '100%' }}>
                        
                        {/* Help Icon on the Left */}
                        <Tooltip title={t('landingPage.hero.helpTooltip', "Learn how to use the verification tools")}>
                            <IconButton 
                                onClick={() => openDocViewer('user_guides/homepage_audit_tools.md')}
                                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' }, mr: 0.5 }}
                            >
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>

                        <TextField
                            variant="outlined"
                            placeholder={t('landingPage.hero.searchIdPlaceholder', 'Enter short Vetra-ID...')}
                            value={searchId}
                            spellCheck="false"
                            onChange={(e) => {
                                const val = e.target.value;
                                setSearchId(val);
                                if (statusMessage) setStatusMessage(''); 
                                
                                // Validation: Prevent users from pasting full URLs here
                                if (val.includes('http') || val.includes('/') || val.includes('vetra-ai.com')) {
                                    setIdValidationError(t('landingPage.hero.idValidationError', 'Please enter the short ID code (e.g., 5G1UJhiU), not a full URL.'));
                                } else if (val.length > 8) {
                                    setIdValidationError(t('landingPage.hero.idLengthError', 'A Vetra-ID must be exactly 8 characters long.'));
                                } else {
                                    setIdValidationError('');
                                }
                            }}
                            className="hero-search-input"
                            fullWidth
                            autoComplete="off"
                            disabled={isProcessing}
                            InputProps={{
                                startAdornment: searchId ? (
                                    <InputAdornment position="start" sx={{ mr: 1, display: 'flex', gap: 0.5 }}>
                                        <Tooltip title={t('landingPage.hero.clearBtn', 'Clear')}>
                                            <Button 
                                                onClick={() => { 
                                                    setSearchId(''); 
                                                    setIdValidationError(''); 
                                                    setStatusMessage('');
                                                    setIsError(false);
                                                }} 
                                                sx={clearButtonStyles}
                                            >
                                                <CloseIcon fontSize="small" />
                                            </Button>
                                        </Tooltip>
                                    </InputAdornment>
                                ) : null
                            }}
                        />
                        <Button 
                            type="submit" 
                            variant="contained" 
                            disableElevation
                            className="hero-search-btn"
                            disabled={isProcessing || !searchId.trim() || !!idValidationError}
                            sx={{ ml: 1, '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12) !important', color: 'rgba(255,255,255,0.3) !important' } }}
                        >
                            {isProcessing ? t('landingPage.hero.processing', 'Processing...') : t('landingPage.hero.searchButton', 'Verify')}
                        </Button>
                    </form>
                )}

                {/* TAB 1: MEDIA AUDIT (URL OR DROP) */}
                {activeTab === 1 && (
                    <form 
                        onSubmit={handleMediaSubmit} 
                        className="hero-search-form" 
                        style={{ maxWidth: '100%', ...(isDragOver ? { borderColor: '#4fc3f7', borderStyle: 'dashed', backgroundColor: 'rgba(79, 195, 247, 0.15)' } : {}) }}
                    >

                        {/* Help Icon on the Left */}
                        <Tooltip title={t('landingPage.hero.helpTooltip', "Learn how to use the verification tools")}>
                            <IconButton 
                                onClick={() => openDocViewer('user_guides/homepage_audit_tools.md')}
                                sx={{ color: 'rgba(255,255,255,0.5)', '&:hover': { color: 'white' }, mr: 0.5 }}
                            >
                                <HelpOutlineIcon />
                            </IconButton>
                        </Tooltip>

                        {/* File Chip OR URL Input */}
                        {mediaFileName ? (
                            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', pl: 1, pr: 1, overflow: 'hidden', gap: 1 }}>
                                <Tooltip title={t('landingPage.hero.clearBtn', 'Clear')}>
                                    <Button onClick={clearMedia} sx={clearButtonStyles}>
                                        <CloseIcon fontSize="small" />
                                    </Button>
                                </Tooltip>
                                <Chip 
                                    label={mediaFileName} 
                                    sx={{ 
                                        maxWidth: '100%', 
                                        color: '#fff', 
                                        backgroundColor: 'rgba(255,255,255,0.15)',
                                        border: '1px solid rgba(255,255,255,0.3)',
                                        fontWeight: 500
                                    }}
                                />
                            </Box>
                        ) : (
                            <TextField
                                variant="outlined"
                                placeholder={t('landingPage.hero.mediaPlaceholder', 'Paste Image/Video URL or Drop File...')}
                                value={typeof mediaInput === 'string' ? mediaInput : ''}
                                spellCheck="false"
                                onChange={(e) => {
                                    setMediaInput(e.target.value);
                                    if (statusMessage) setStatusMessage(''); 
                                    if (showDeepfakeCTA) setShowDeepfakeCTA(false);
                                    setHasAudited(false);
                                }}
                                className="hero-search-input"
                                fullWidth
                                autoComplete="off"
                                disabled={isProcessing}
                                InputProps={{
                                    startAdornment: (mediaInput && typeof mediaInput === 'string') ? (
                                        <InputAdornment position="start" sx={{ mr: 1, display: 'flex', gap: 0.5 }}>
                                            <Tooltip title={t('landingPage.hero.clearUrlBtn', 'Clear URL')}>
                                                <Button onClick={clearMedia} sx={clearButtonStyles}>
                                                    <CloseIcon fontSize="small" />
                                                </Button>
                                            </Tooltip>
                                        </InputAdornment>
                                    ) : null
                                }}
                                sx={{ '& .MuiInputBase-input': { textOverflow: 'ellipsis' } }}
                            />
                        )}

                        <input 
                            type="file" 
                            ref={fileInputRef} 
                            style={{ display: 'none' }} 
                            accept="image/*,video/*"
                            onChange={(e) => {
                                if (e.target.files && e.target.files.length > 0) {
                                    handleFileSelection(e.target.files[0]);
                                }
                            }}
                        />
                        
                        {!mediaFileName && (
                            <Tooltip title={t('landingPage.hero.uploadFile', "Upload File")}>
                                <IconButton onClick={() => fileInputRef.current.click()} disabled={isProcessing} sx={{ color: 'rgba(255,255,255,0.5)', mr: 1 }}>
                                    <UploadFileIcon />
                                </IconButton>
                            </Tooltip>
                        )}

                        <Tooltip title={hasAudited ? t('landingPage.hero.auditDisabledTooltip', "Please clear the current file/URL to audit a new one.") : ""}>
                            <span>
                                <Button 
                                    type="submit" 
                                    variant="contained" 
                                    disableElevation
                                    className="hero-search-btn"
                                    endIcon={<SearchIcon />}
                                    disabled={isProcessing || (!mediaInput && !mediaFileName) || hasAudited}
                                    sx={{ ml: 1, '&.Mui-disabled': { backgroundColor: 'rgba(255,255,255,0.12) !important', color: 'rgba(255,255,255,0.3) !important' } }}
                                >
                                    {isProcessing ? t('landingPage.hero.processing', 'Scanning...') : t('landingPage.hero.auditButton', 'Audit')}
                                </Button>
                            </span>
                        </Tooltip>
                    </form>
                )}

                {/* STATUS MESSAGE OR VALIDATION ERROR DISPLAY */}
                {((idValidationError && activeTab === 0) || statusMessage) && (
                    <Box sx={{ minHeight: '24px', mt: 1, textAlign: 'center' }}>
                        {idValidationError && activeTab === 0 ? (
                            <Typography variant="caption" sx={{ color: '#ff5252', fontWeight: 500, animation: 'fadeIn 0.3s ease-in-out' }}>
                                {idValidationError}
                            </Typography>
                        ) : (
                            <Typography 
                                variant="caption" 
                                sx={{ 
                                    color: isError ? '#ff5252' : '#4caf50',
                                    fontWeight: 500, 
                                    animation: 'fadeIn 0.3s ease-in-out'
                                }}
                            >
                                {statusMessage}
                            </Typography>
                        )}
                    </Box>
                )}

                {/* COMPACT LEAD GEN BANNER */}
                {showDeepfakeCTA && (
                    <Box sx={{ 
                        mt: 1, p: 1.5, 
                        background: 'rgba(255, 152, 0, 0.1)', 
                        border: '1px solid rgba(255, 152, 0, 0.3)', 
                        borderRadius: '8px', 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center', 
                        flexDirection: { xs: 'column', sm: 'row' },
                        textAlign: { xs: 'center', sm: 'left' },
                        animation: 'fadeIn 0.4s ease-in-out',
                        backdropFilter: 'blur(5px)',
                        gap: 2,
                        width: '100%'
                    }}>
                        <Box sx={{ flexGrow: 1 }}>
                            <Typography variant="subtitle2" sx={{ color: '#ffb74d', fontWeight: 'bold', lineHeight: 1.2 }}>
                                {ctaType === 'audio_tampered' 
                                    ? t('landingPage.hero.audioTamperedTitle', 'Run Deepfake Scanner') 
                                    : t('landingPage.hero.watermarkNotFoundTitle', 'No Brand Watermark Detected')
                                }
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                {ctaType === 'audio_tampered' 
                                    ? t('landingPage.hero.audioTamperedDesc', 'Analyze this file to identify the synthetic audio source.') 
                                    : t('landingPage.hero.watermarkNotFoundDesc', 'This asset is unsecured. Check for Deepfakes instead.')
                                }
                            </Typography>
                        </Box>
                        <Button 
                            variant="contained" 
                            color="warning" 
                            size="small"
                            onClick={() => {
                                if (typeof mediaInput === 'string' && mediaInput.trim()) {
                                    const isVideo = /\.(mp4|webm|mov|avi|mkv|m4v)(\?.*)?$/i.test(mediaInput);
                                    const detectedType = isVideo ? 'video' : 'image';
                                    navigate(`/app?auto_analyze_url=${encodeURIComponent(mediaInput.trim())}&auto_analyze_type=${detectedType}`);
                                } else if (mediaInput instanceof File) {
                                    const detectedType = mediaInput.type.startsWith('video/') ? 'video' : 'image';
                                    navigate('/app', { state: { autoFile: mediaInput, autoType: detectedType } });
                                } else {
                                    navigate('/app');
                                }
                            }}
                            sx={{ fontWeight: 'bold', whiteSpace: 'nowrap', minWidth: 'fit-content' }}
                        >
                            {t('landingPage.hero.watermarkNotFoundBtn', 'Scan File')}
                        </Button>
                    </Box>
                )}

            </motion.div>
        </Box>
    );
};

const LandingPage = () => {
    const { t } = useTranslation();
    const containerRef = useRef(null);
    const [isScrolled, setIsScrolled] = useState(false);

    // Prevent jumping on refresh by targeting the container's scroll position, not the window's
    useEffect(() => {
        const container = containerRef.current;
        if (container) {
            container.scrollTop = 0;
        }
    }, []);

    // Listen to the container's scroll event, not the window
    const handleScroll = (e) => {
        if (e.target.scrollTop > 20) {
            setIsScrolled(true);
        } else {
            setIsScrolled(false);
        }
    };

    const particlesInit = useCallback(async (engine) => {
        await loadSlim(engine);
    }, []);

    // Full particle configuration object
    const particlesConfig = {
        background: {
            color: {
                value: 'transparent',
            },
        },
        fpsLimit: 120,
        interactivity: {
            events: {
                onHover: {
                    enable: true,
                    mode: 'repulse',
                },
                resize: true,
            },
            modes: {
                repulse: {
                    distance: 100,
                    duration: 0.4,
                },
            },
        },
        particles: {
            color: {
                value: '#ffffff',
            },
            links: {
                color: '#4fc3f7',
                distance: 150,
                enable: true,
                opacity: 0.4,
                width: 1,
            },
            move: {
                direction: 'none',
                enable: true,
                outModes: {
                    default: 'bounce',
                },
                random: false,
                speed: 1,
                straight: false,
            },
            number: {
                density: {
                    enable: true,
                    area: 800,
                },
                value: 80,
            },
            opacity: {
                value: 0.5,
            },
            shape: {
                type: 'circle',
            },
            size: {
                value: { min: 1, max: 3 },
            },
        },
        detectRetina: true,
    };

    // MOTION: 3. Get the headline text to be animated
    const headlineText = t('landingPage.hero.headline');

    return (
        <div 
            ref={containerRef}
            onScroll={handleScroll}
            className={`landing-page-container ${isScrolled ? 'scrolled' : ''}`}
        >
            <Particles id="tsparticles" init={particlesInit} options={particlesConfig} />
            <Helmet>
                <title>{t('landingPage.helmet.title')}</title>
                <meta name="description" content={t('landingPage.helmet.description')} />
            </Helmet>
            <div className="sticky-nav-wrapper">
                <UniversalNav />
            </div>
            
            <main className="landing-page-content-area">
                
                {/* 1. HERO SECTION */}
                <div className="snap-section">
                    <motion.section 
                        className="hero-section"
                        variants={staggerContainer}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.2 }}
                    >
                        <motion.h1
                            className="hero-headline" 
                            variants={sentenceVariants}
                            initial="hidden"
                            animate="visible"
                        >
                            {headlineText.split('').map((char, index) => (
                                <motion.span key={char + '-' + index} variants={letterVariants}>
                                    {char}
                                </motion.span>
                            ))}
                        </motion.h1>

                        <DynamicHeroImage />

                        <Typography variant="h5" component="p" gutterBottom className="hero-subheadline">
                            {t('landingPage.hero.subheadline')}
                        </Typography>

                        <Box className="hero-actions">
                            <Button 
                                component={RouterLink} 
                                to="/login" 
                                variant="contained" 
                                size="large" 
                                className="hero-cta-button"
                            >
                                {t('landingPage.hero.cta')}
                            </Button>
                        </Box>
                        <VetraIdSearch />
                        
                        {/* SCROLL INDICATOR */}
                        <div className="scroll-indicator-wrapper">
                            <motion.div 
                                className="scroll-indicator"
                                animate={{ y: [0, 10, 0] }}
                                transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}
                                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                                onClick={() => {
                                    const container = document.querySelector('.landing-page-container');
                                    const nextSection = document.querySelectorAll('.snap-section')[1]; // Target the wrapper itself
                                    if (container && nextSection) {
                                        container.scrollTo({ top: nextSection.offsetTop, behavior: 'smooth' });
                                    }
                                }}
                            >
                                <ExpandMoreIcon sx={{ fontSize: '3rem' }} />
                            </motion.div>
                        </div>
                    </motion.section>
                </div>

                {/* 2. PROBLEM SECTION */}
                <div className="snap-section">
                    <motion.section 
                        className="problem-section content-box"
                        variants={sectionVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.45 }}
                    >
                        <Typography variant="h4" component="h2" className="section-title">
                            {t('landingPage.problem.title')}
                        </Typography>
                        
                        <FraudTicker />

                        <Typography variant="body1" className="section-subtitle" sx={{ marginBottom: '1.5rem !important' }}>
                            {t('landingPage.problem.subtitle')}
                        </Typography>

                        <Typography variant="h5" component="p" className="trust-layer-text">
                            {t('landingPage.problem.trustLayer', 'Vetra AI - The Layer of Trust in Digital')}
                        </Typography>
                        
                        <div className="problem-grid" style={{ marginTop: '3rem' }}>
                            <div className="problem-card">
                                <DangerousIcon className="problem-card-icon" />
                                <Typography variant="h6" component="h3">{t('landingPage.problem.cards.erosion.title')}</Typography>
                                <Typography variant="body2" className="card-text">{t('landingPage.problem.cards.erosion.text')}</Typography>
                            </div>
                            <div className="problem-card">
                                <TrendingUpIcon className="problem-card-icon" />
                                <Typography variant="h6" component="h3">{t('landingPage.problem.cards.fraud.title')}</Typography>
                                <Typography variant="body2" className="card-text">{t('landingPage.problem.cards.fraud.text')}</Typography>
                            </div>
                            <div className="problem-card">
                                <SecurityIcon className="problem-card-icon" />
                                <Typography variant="h6" component="h3">{t('landingPage.problem.cards.reputation.title')}</Typography>
                                <Typography variant="body2" className="card-text">{t('landingPage.problem.cards.reputation.text')}</Typography>
                            </div>
                        </div>
                    </motion.section>
                </div>

                {/* 3. FEATURES SECTION */}
                <div className="snap-section">
                    <motion.section 
                        className="features-preview-section content-box"
                        variants={sectionVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.45 }}
                    >
                        <Typography variant="h4" component="h2" className="section-title">
                            {t('landingPage.features.title')}
                        </Typography>
                        <Typography variant="body1" className="section-subtitle">
                            {t('landingPage.features.subtitle')}
                        </Typography>
                        
                        <div className="features-grid">
                            <div className="feature-card">
                                <TextFieldsIcon className="feature-icon" />
                                <Typography variant="h6" component="h3">
                                    {t('landingPage.features.textTitle')}
                                </Typography>
                                <Typography variant="body2" className="card-text">
                                    {t('landingPage.features.textDescription')}
                                </Typography>
                            </div>
                            <div className="feature-card">
                                <ImageIcon className="feature-icon" />
                                <Typography variant="h6" component="h3">
                                    {t('landingPage.features.imageTitle')}
                                </Typography>
                                <Typography variant="body2" className="card-text">
                                    {t('landingPage.features.imageDescription')}
                                </Typography>
                            </div>
                            <div className="feature-card">
                                <TheatersIcon className="feature-icon" />
                                <Typography variant="h6" component="h3">
                                    {t('landingPage.features.videoTitle')}
                                </Typography>
                                <Typography variant="body2" className="card-text">
                                    {t('landingPage.features.videoDescription')}
                                </Typography>
                            </div>
                        </div>
                    </motion.section>
                </div>

                {/* 4. TRUST SECTION */}
               <div className="snap-section">
                    <motion.section 
                        className="trust-section content-box"
                        variants={sectionVariants}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: false, amount: 0.45 }}
                    >
                        <Typography variant="h4" component="h2" className="section-title">
                            {t('landingPage.trust.title')}
                        </Typography>
                        <Typography variant="body1" className="section-subtitle">
                            {t('landingPage.trust.subtitle')}
                        </Typography>
                        <div className="trust-grid">
                            <div className="trust-card">
                                <ShieldIcon className="trust-icon" />
                                <Typography variant="h6" component="h3">{t('landingPage.trust.secureTitle')}</Typography>
                                <Typography variant="body2" className="card-text">{t('landingPage.trust.secureDescription')}</Typography>
                            </div>
                            <div className="trust-card">
                                <ImageIcon className="trust-icon" />
                                <Typography variant="h6" component="h3">
                                    {t('landingPage.trust.accuracyTitle')}
                                </Typography>
                                <Typography variant="body2" className="card-text">
                                    {t('landingPage.trust.accuracyDescription')}
                                </Typography>
                            </div>
                            <div className="trust-card">
                                <TheatersIcon className="trust-icon" />
                                <Typography variant="h6" component="h3">
                                    {t('landingPage.trust.apiTitle')}
                                </Typography>
                                <Typography variant="body2" className="card-text">
                                    {t('landingPage.trust.apiDescription')}
                                </Typography>
                            </div>
                        </div>
                    </motion.section>
                </div>

            </main>
        </div>
    );
};

export default LandingPage;